import React, { FC, useState } from "react"

import { LoadingOutlined } from "@ant-design/icons"
import {
  Button,
  Card,
  Form,
  Input,
  Row,
  Space,
  Spin,
  DatePicker, Switch,
  Col,
  Select,
  InputNumber
} from "antd"

import Counter from "../../../common/components/counter/Counter"
import AutoWidthSelect from "../../../common/components/selects/AutoWidthSelect"
import { defaultStockFBSFilters } from "../../../common/constants/filters"
import { useActions } from "../../../common/hooks/useActions"
import { useTypedSelector } from "../../../common/hooks/useTypedSelector"
import { MarketPlace, OrderStatus } from "../../../types/api"
import { marketPlacePictureMap, removeObjectEmptyKeys } from "../../../utils/data"


interface Props {
  isLoading: boolean
  sorting: { [x: string]: string }
  setFilters: (v: any) => any
}

/** Склад из `filters.warehouses`; `is_active` бэк отдаёт с 10.09 (активен при status_id = 2) */
interface FilterWarehouse {
  id: number
  name: string
  status_id?: number
  is_active?: boolean
}

/** Цвет неактивного склада в списке */
const INACTIVE_WAREHOUSE_COLOR = "#bfbfbf"
/** Подсказка при наведении на неактивный склад */
const INACTIVE_WAREHOUSE_HINT = "Склад неактивен — заказы на него не поступают"

/**
 * Неактивные склады — в конец списка: заказы на них не кладутся, и выбор такого
 * склада даёт пустую таблицу. Порядок внутри групп сохраняется как пришёл с бэка.
 * Если бэк флаг не прислал, склад считается активным.
 */
const sortWarehousesActiveFirst = (warehouses: FilterWarehouse[]): FilterWarehouse[] => [
  ...warehouses.filter((warehouse) => warehouse.is_active !== false),
  ...warehouses.filter((warehouse) => warehouse.is_active === false),
]

/** Счётчики карточек «ОТГРУЗКА:»; бэк считает их без учёта `supply_formed` */
interface SupplyFormedCount {
  formed: number
  not_formed: number
}

/** Карточка блока «ОТГРУЗКА:» — значение фильтра `supply_formed` и её счётчик */
interface SupplyFormedCard {
  title: string
  value: boolean
  countKey: keyof SupplyFormedCount
}

/**
 * Заказы OZON в «Собран» разводятся по отгрузке: OZON держит отправление
 * в «Ожидает отгрузки», пока его не заберёт курьер, поэтому по статусу их не отличить.
 */
const SUPPLY_FORMED_CARDS: SupplyFormedCard[] = [
  { title: "К отгрузке", value: false, countKey: "not_formed" },
  { title: "Сформированы", value: true, countKey: "formed" },
]

/** Карточки, при смене которых вкладка «ОТГРУЗКА:» выставляется заново */
const SUPPLY_FORMED_RESET_KEYS = [ "status_id", "marketplace_id", "partner_id" ]

/** Выбранные карточки фильтра до первого клика и после «Сбросить» */
const INITIAL_ACTIVE_CARDS = {
  partner_id: null,
  marketplace_id: MarketPlace.WB,
  status_id: null,
  supply_formed: undefined,
  not_collected: false,
  no_label: false,
  error_on_marketplace: false,
}

/** Блок «ОТГРУЗКА:» нужен только у OZON во вкладке «Собран» */
const isSupplyFormedAvailable = (cards: { marketplace_id?: number | null, status_id?: number | null }): boolean =>
  cards.marketplace_id === MarketPlace.OZON && cards.status_id === OrderStatus.Collected

/**
 * Значение `supply_formed` после смены статуса, маркетплейса или кабинета:
 * у OZON в «Собран» сразу «К отгрузке» (складу нужны именно эти заказы),
 * иначе фильтр снимается — на другой вкладке он дал бы пустую таблицу.
 */
const getDefaultSupplyFormed = (cards: { marketplace_id?: number | null, status_id?: number | null }): boolean | undefined =>
  isSupplyFormedAvailable(cards) ? false : undefined

const FilterHeading = ({ text }:{text:string}) => (
  <p style={{ fontWeight:600,fontSize:16 }}>{text}</p>
)

const FilterTable: FC<Props> = ({ isLoading, setFilters, sorting }) => {
  const [ form ] = Form.useForm()
  /** Выбранный склад: счётчики статусов бэк считает без него, поэтому нужна оговорка */
  const selectedWarehouseId = Form.useWatch("warehouse_id", form)
  const { pagesData, filters, filterList, itemData } = useTypedSelector((state) => state.stockFBS)
  const { stockFBSGetList } = useActions()
  const [ activeCards, setActiveCards ] = useState<any>(INITIAL_ACTIVE_CARDS)
  const supplyFormedCount: SupplyFormedCount | undefined = filters?.supply_formed_count

  const onSubmitFilter = (values: any) => {
    const cleanedValues = removeObjectEmptyKeys(values)
    const data = {
      ...cleanedValues,
      ...(values.delivery_date && { delivery_date: values.delivery_date.format("YYYY-MM-DD") }),
      ...(values.arrival_date && { arrival_date: {
        start: cleanedValues.arrival_date[0].format("YYYY.MM.DD"),
        end: cleanedValues.arrival_date[1].format("YYYY.MM.DD"),
      } }),
      ...(values.assembly_date && { assembly_date: {
        start: cleanedValues.assembly_date[0].format("YYYY.MM.DD"),
        end: cleanedValues.assembly_date[1].format("YYYY.MM.DD"),
      } }),
      user_id: cleanedValues.user_id?.length ? cleanedValues.user_id : undefined,
    }

    setFilters(data)
    stockFBSGetList({
      page: pagesData.currentPage,
      pageSize: pagesData.pageSize,
      sorting: sorting,
      filters: data
    })
  }

  const handleCardClick = (key: string, value: any) => {
    // Повторный клик снимает выбор; маркетплейс снять нельзя, он остаётся выбранным
    const deselectedValue = key === "marketplace_id" ? value : null
    const nextValue = activeCards[key] === value ? deselectedValue : value
    const nextCards = { ...activeCards, [key]: nextValue }

    // Строго через `false`/`undefined`: `false` — это выбранная карточка «К отгрузке»
    if (SUPPLY_FORMED_RESET_KEYS.includes(key)) {
      nextCards.supply_formed = getDefaultSupplyFormed(nextCards)
    }

    form.setFieldsValue({ [key]: nextValue, supply_formed: nextCards.supply_formed })
    setActiveCards(nextCards)
    form.submit()
  }
  const marketPlaces = [
    {
      id:1,
      title:"WILDBERRIES"
    },
    {
      id:2,
      title:"OZON"
    },
  ]

  return (
    <Spin
      spinning={isLoading}
      indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
    >
      <Card style={{ width: "100%",backgroundColor:"rgba(0, 0, 0, 0.02)" }}>
        <Form form={form} onFinish={onSubmitFilter} name="basic">
          {
            filters?.partners?.length > 0 && <>
              <FilterHeading text={"ОРГАНИЗАЦИЯ:"} />
              <Row gutter={16}>
                <Form.Item name="partner_id">
                  <div style={{ display: "flex", rowGap: 20, columnGap: 10, flexWrap: "wrap" }}>
                    {
                      filters.partners.map((partner: {
                        partner_name: string,
                        records_count: number,
                        partner_id: number
                      }) => (
                        <Card
                          bodyStyle={{
                            padding:"12px"
                          }}
                          onClick={() => handleCardClick("partner_id", partner.partner_id)}
                          className={activeCards.partner_id === partner.partner_id ? "active-card" : "custom-card"}
                        >
                          {partner.partner_name}
                          <Counter count={partner?.records_count} />
                        </Card>
                      ))
                    }
                  </div>
                </Form.Item>
              </Row>
              {
                selectedWarehouseId && (
                  <span style={{ color: "#888", marginTop: "-8px", display: "block" }}>
                    Счётчики посчитаны по всем складам кабинета, а таблица — по выбранному складу.
                  </span>
                )
              }
            </>
          }
          <div>
            <FilterHeading text={"Склады:"} />
            <div style={{ marginLeft: "-7px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <div style={{ display: "flex", gap: "10px" }}>
                <Form.Item style={{ width: "400px" }} name="warehouse_id">
                  <Select
                    placeholder="Склад"
                    showSearch
                    filterOption={(input, option) =>
                      option && option.children
                        ? (option.children as unknown as string)
                          .toLowerCase().indexOf(input.toLowerCase()) >= 0
                        : false
                    }
                    defaultActiveFirstOption={false}
                    allowClear
                  >
                    {
                      sortWarehousesActiveFirst(filters.warehouses || []).map((warehouse) => (
                        <Select.Option
                          key={warehouse.id}
                          value={warehouse.id}
                          title={warehouse.is_active === false ? INACTIVE_WAREHOUSE_HINT : warehouse.name}
                          style={warehouse.is_active === false ? { color: INACTIVE_WAREHOUSE_COLOR } : undefined}
                        >
                          {warehouse.name}
                        </Select.Option>
                      ))
                    }
                  </Select>
                </Form.Item>
              </div>
            </div>
          </div>
          <FilterHeading text={"Даты:"} />
          <div style={{ marginLeft: "-7px", display: "flex", gap: "10px",flexWrap:"wrap" }}>
            <div style={{ display: "flex", gap: "10px" }}>
              {
                activeCards.marketplace_id === 2 && <Form.Item name="delivery_date">
                  <DatePicker format="YYYY-MM-DD" placeholder={"Дата доставки"}/>
                </Form.Item>
              }
              <div className={"hide-datepicker-placeholder"}>

                <Form.Item name="arrival_date">
                  <DatePicker.RangePicker placeholder={[ "Дата заказа", "заказа" ]} style={{ width: "230px" }}
                    format="DD.MM.YYYY"/>
                </Form.Item>
              </div>
              <div className={"hide-datepicker-placeholder"}>
                <Form.Item name="assembly_date">
                  <DatePicker.RangePicker style={{ width: "234px" }} placeholder={[ "Дата сборки", "сборки" ]}
                    format="DD.MM.YYYY"/>
                </Form.Item>
              </div>

            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <Form.Item style={{ width: "max-content" }} name="order_id">
                <Input placeholder="Номер заказа" allowClear={true} />
              </Form.Item>
              <Form.Item style={{ width: "max-content" }} name="supply_id">
                <AutoWidthSelect showSearch={true} placeholder="Поставка" allowClear={true} options={filters?.supplies} />
              </Form.Item>
            </div>
          </div>
          <div style={{ display:"flex", gap:"15px", flexWrap:"wrap" }}>
            <div>
              <FilterHeading text={"Артикул цифровой:"} />
              <div style={{ marginLeft: "-7px", display: "flex", gap: "10px",flexWrap:"wrap" }}>
                <div style={{ display: "flex", gap: "10px" }}>
                  <Form.Item style={{ width: "max-content" }} name="nm_id">
                    <Input placeholder="Артикул цифровой" allowClear={true}/>
                  </Form.Item>
                </div>
              </div>
            </div>
            <div>
              <FilterHeading text={"Артикул буквенный:"} />
              <div style={{ marginLeft: "-7px", display: "flex", gap: "10px",flexWrap:"wrap" }}>
                <div style={{ display: "flex", gap: "10px" }}>
                  <Form.Item style={{ width: "max-content" }} name="nm_name">
                    <Input placeholder="Артикул буквенный" allowClear={true}/>
                  </Form.Item>
                </div>
              </div>
            </div>
            <div>
              <FilterHeading text={"Номер стикера:"} />
              <div style={{ marginLeft: "-7px", display: "flex", gap: "10px",flexWrap:"wrap" }}>
                <div style={{ display: "flex", gap: "10px" }}>
                  <Form.Item style={{ width: "max-content" }} name="sticker_number">
                    <Input placeholder="Номер стикера" allowClear={true}/>
                  </Form.Item>
                </div>
              </div>
            </div>
            <div>
              <FilterHeading text={"Сотрудник:"} />
              <div style={{ marginLeft: "-7px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <div style={{ display: "flex", gap: "10px" }}>
                  <Form.Item style={{ width: "200px" }} name="user_id">
                    <Select
                      placeholder="Сотрудник"
                      mode="multiple"
                      showSearch
                      filterOption={(input, option) =>
                        option && option.children
                          ? (option.children as unknown as string)
                            .toLowerCase().indexOf(input.toLowerCase()) >= 0
                          : false
                      }
                      defaultActiveFirstOption={false}
                    >
                      {
                        (filterList.users_list || [])?.map((user: { id: number; name: string }) => (
                          <Select.Option key={user.id} value={user.id}>
                            {user.name}
                          </Select.Option>
                        ))
                      }
                    </Select>
                  </Form.Item>
                </div>
              </div>
            </div>
            <div>
              <FilterHeading text={"Поставщик:"} />
              <div style={{ marginLeft: "-7px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <div style={{ display: "flex", gap: "10px" }}>
                  <Form.Item style={{ width: "200px" }} name="purveyor_id">
                    <Select
                      placeholder="Поставщик"
                      showSearch
                      filterOption={(input, option) =>
                        option && option.children
                          ? (option.children as unknown as string)
                            .toLowerCase().indexOf(input.toLowerCase()) >= 0
                          : false
                      }
                      defaultActiveFirstOption={false}
                      allowClear
                    >
                      {Object.entries(filterList?.suppliers_list || {}).map(([ key, value ]) => (
                        <Select.Option key={key} value={Number(key)}>
                          {value as string}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </div>
            </div>     
            <div>
              <FilterHeading text={"Кол-во остатков:"} />
              <div style={{ marginLeft: "-7px", display: "flex", gap: "10px",flexWrap:"wrap" }}>
                <div style={{ display: "flex", gap: "10px" }}>
                  <Form.Item style={{ width: "150px" }} name="stock_from">
                    <InputNumber
                      placeholder="От"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                  <Form.Item style={{ width: "150px" }} name="stock_to">
                    <InputNumber
                      placeholder="До"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </div>
              </div>
            </div>
          </div>
          <FilterHeading text={"МАРКЕТПЛЕЙС:"}/>
          <Row gutter={16}>
            <Form.Item name="marketplace_id" initialValue={1}>
              <div style={{ display: "flex", rowGap: 20, columnGap: 10 }}>
                {marketPlaces.map((market) => (
                  <Card
                    bodyStyle={{
                      padding: "12px"
                    }}
                    onClick={() => handleCardClick("marketplace_id", market.id)}
                    className={activeCards.marketplace_id === market.id ? "active-card" : "custom-card"}
                  >
                    <img alt={"Маркетплейс"} style={{ borderRadius: "50%", width: "36px" }}
                      src={marketPlacePictureMap[market.id]}/> {market.title}
                    <Counter count={filters?.marketplace_count?.[market.id === 1 ? "wb" : "ozon"]}/>
                  </Card>
                ))}
              </div>
            </Form.Item>

          </Row>
          {
            filters?.statuses?.length > 0 && <>
              <FilterHeading text={"СТАТУС:"} />
              <Row gutter={16}>
                <Form.Item name="status_id">
                  <div style={{ display: "flex", rowGap: 20, columnGap: 10, flexWrap: "wrap" }}>
                    {
                      filters.statuses.map((status: {
                          name: string,
                          count: number,
                          id: number
                        }) => (
                        <Card
                          bodyStyle={{
                            padding:"12px"
                          }}
                          onClick={() => handleCardClick("status_id", status.id)}
                          className={activeCards.status_id === status.id ? "active-card" : "custom-card"}
                        >
                          {status.name}
                          <Counter count={status?.count} />
                        </Card>
                      ))
                    }
                  </div>
                </Form.Item>
              </Row>
            </>
          }
          {/*
            Поле зарегистрировано всегда: при клике по «Собран» блок ниже ещё не
            отрисован, а форма отправляет только смонтированные поля — иначе первый
            запрос ушёл бы без `supply_formed: false`.
          */}
          <Form.Item name="supply_formed" hidden>
            <Input />
          </Form.Item>
          {
            isSupplyFormedAvailable(activeCards) && <>
              <FilterHeading text={"ОТГРУЗКА:"} />
              <Row gutter={16} style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", rowGap: 20, columnGap: 10, flexWrap: "wrap" }}>
                  {
                    SUPPLY_FORMED_CARDS.map((card) => (
                      <Card
                        key={card.countKey}
                        bodyStyle={{
                          padding:"12px"
                        }}
                        onClick={() => handleCardClick("supply_formed", card.value)}
                        className={activeCards.supply_formed === card.value ? "active-card" : "custom-card"}
                      >
                        {card.title}
                        <Counter count={supplyFormedCount?.[card.countKey] ?? 0} />
                      </Card>
                    ))
                  }
                </div>
              </Row>
            </>
          }
          <div style={{ marginLeft: "-8px",display:"flex",columnGap:"20px",alignItems:"center",rowGap:"10px",flexWrap:"wrap" }}>
            <Form.Item style={{ width: "max-content" }} name="not_collected" valuePropName="checked">
              <span style={{ marginRight:"10px" }}>Не собирался</span>
              <Switch
                checked={activeCards.not_collected}
                onChange={(checked) => handleCardClick("not_collected", checked)}
              />
            </Form.Item>
            <Form.Item style={{ width: "max-content" }} name="no_label" valuePropName="checked">
              <span style={{ marginRight:"10px" }}>Без этикетки</span>
              <Switch
                checked={activeCards.no_label}
                onChange={(checked) => handleCardClick("no_label", checked)}
              />
            </Form.Item>
            <Form.Item style={{ width: "max-content" }} name="error_on_marketplace" valuePropName="checked">
              <span style={{ marginRight:"10px" }}>Ошибка Маркетплейса</span>
              <Switch
                checked={activeCards.error_on_marketplace}
                onChange={(checked) => handleCardClick("error_on_marketplace", checked)}
              />
            </Form.Item>
            <Form.Item style={{ width: "max-content" }} name="is_cis_marked" valuePropName="checked">
              <span style={{ marginRight:"10px" }}>Нужна маркировка</span>
              <Switch
                checked={activeCards.is_cis_marked}
                onChange={(checked) => handleCardClick("is_cis_marked", checked)}
              />
            </Form.Item>
          </div>
        </Form>
        <Space style={{ marginTop: "16px" }}>
          <Button className={"blueButton"} type="primary" style={{ background:"#00a2e8" }} onClick={() => form.submit()}>
              Применить
          </Button>
          <Button
            type="default"
            onClick={() => {
              form.resetFields()
              // Подсветка карточек должна совпадать с очищенной формой
              setActiveCards(INITIAL_ACTIVE_CARDS)
              stockFBSGetList({
                page: pagesData.currentPage || 1,
                pageSize: pagesData.pageSize || 10,
                sorting: sorting,
                filters: defaultStockFBSFilters
              })
              setFilters(defaultStockFBSFilters)
            }}
          >
              Сбросить
          </Button>
        </Space>
      </Card>
    </Spin>
  )
}

export default FilterTable
